import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, FolderKanban, Trash2, Calendar } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#00E5FF", "#8A52FF", "#34D399", "#FBBF24", "#F472B6"];

export default function Projects() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [color, setColor] = useState(COLORS[0]);

  const load = () => api.get("/projects").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Name required");
    await api.post("/projects", { name, description: desc, color });
    setOpen(false);
    setName(""); setDesc(""); setColor(COLORS[0]);
    load();
    toast.success("Project created");
  };

  const remove = async (id) => {
    await api.delete(`/projects/${id}`);
    load();
    toast.success("Project deleted");
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between">
        <div>
          <div className="kr-overline">[ WORKSPACE ]</div>
          <h1 className="font-display text-4xl mt-2 tracking-tight">Projects</h1>
          <p className="text-white/55 text-sm mt-1.5">Group generations, files and notes into focused workstreams.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 rounded-full" data-testid="new-project-btn"><Plus className="h-4 w-4 mr-2" /> New project</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0E] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-display">New project</DialogTitle>
              <DialogDescription className="text-white/50 text-sm">Group your generations, files and notes into a focused workstream.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="kr-overline">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#050505] border-white/10 mt-2" data-testid="project-name-input" />
              </div>
              <div>
                <Label className="kr-overline">Description</Label>
                <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} className="bg-[#050505] border-white/10 mt-2" rows={3} data-testid="project-desc-input" />
              </div>
              <div>
                <Label className="kr-overline">Color</Label>
                <div className="flex gap-2 mt-2">
                  {COLORS.map((c) => (
                    <button key={c} onClick={() => setColor(c)} className={`w-7 h-7 rounded-full border-2 ${color === c ? "border-white" : "border-white/10"}`} style={{ background: c }} data-testid={`color-${c}`} />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90" data-testid="create-project-confirm-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p, i) => (
          <motion.div key={p.project_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="kr-card p-6 relative group" data-testid={`project-card-${p.project_id}`}>
            <Link to={`/projects/${p.project_id}`} className="absolute inset-0 z-0" aria-label={`Open ${p.name}`} data-testid={`open-project-${p.project_id}`} />
            <div className="relative z-10 pointer-events-none">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 border border-white/10 flex items-center justify-center" style={{ color: p.color }}>
                  <FolderKanban className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(p.project_id); }} className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-red-300 transition pointer-events-auto relative z-20" data-testid={`delete-project-${p.project_id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="font-display text-lg mt-5">{p.name}</div>
              <div className="text-sm text-white/50 mt-1 line-clamp-2 min-h-[2.5rem]">{p.description || "—"}</div>
              <div className="kr-overline mt-5 flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {new Date(p.created_at).toLocaleDateString()}</div>
            </div>
          </motion.div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center py-20 text-white/40">
            <FolderKanban className="h-10 w-10 mx-auto mb-3" />
            <div className="text-sm">No projects yet. Create your first workstream.</div>
          </div>
        )}
      </div>
    </div>
  );
}
